import * as logfire from '@pydantic/logfire-browser';
import { Attr } from '../core/attributes';
import { buildResourceAttributes, type ServiceKey } from '../core/resource';
import { capturesFullPayloads, type EnvBag } from '../core/environment';
import { getTelemetryIdentity, identityAttributes, setTelemetryIdentity } from '../core/identity';
import { setErrorReporter } from '../core/reporter';
import { scrubAttribute, scrubAttributes, scrubText } from '../core/scrub';
import { sanitizeUrl, templatizePath } from '../core/scrub/url';
import { isForeignExtensionError } from './foreignErrors';
import { firstPartyOnly, originPattern } from './ignoreUrls';
import { ClientIdentitySpanProcessor } from './spanIdentity';
import { PruneClientSpanEventsProcessor } from './spanEvents';
import { NotFoundIsNotAnErrorSpanProcessor } from './spanStatus';
import { annotateClientSpan, type MutableClientSpan } from './spanNaming';

/**
 * Ids only. Never name, never email — the browser SDK's default `getUser()` would
 * send both. The holder is shared with the extension (`core/identity`), so both
 * clients emit the same `firmable.*` keys the api already carries.
 */
export {
  clearTelemetryIdentity,
  getTelemetryIdentity,
  setTelemetryIdentity,
  type TelemetryIdentity,
} from '../core/identity';

/** @deprecated Use `setTelemetryIdentity` — it carries the org id too. */
export function setTelemetryUserId(userId: string | undefined): void {
  setTelemetryIdentity({ userId, orgId: getTelemetryIdentity().orgId });
}

export interface BrowserTelemetryOptions {
  service: ServiceKey;
  env: EnvBag;
  /**
   * Same-origin proxy path, e.g. `/_tel/v1/traces`. The write token stays
   * server-side in the proxy, so nothing secret ships in the bundle.
   */
  traceUrl: string;
  /** Origin of apps/api — the only target we propagate trace context to. */
  apiOrigin: string;
  clientSource?: 'app' | 'hubspot';
  /**
   * Fraction of page loads that emit documentLoad plus per-asset resourceFetch
   * spans. This is the real cost lever: resourceTiming.detail only shrinks
   * payload bytes, it does NOT reduce span count.
   */
  documentLoadSampleRate?: number;
}

/**
 * Configure browser RUM.
 *
 * Two things the SDK does NOT do that are handled here: it installs no global
 * error handlers (so window.onerror and unhandledrejection are wired below),
 * and it applies no PII scrubbing to auto-instrumentation spans (so URL and
 * user attributes are overridden).
 */
export function configureBrowserTelemetry(options: BrowserTelemetryOptions): () => Promise<void> {
  const resource = buildResourceAttributes({
    service: options.service,
    env: options.env,
    clientSource: options.clientSource ?? 'app',
  });

  const apiPattern = originPattern(options.apiOrigin);
  const documentLoadEnabled = Math.random() < (options.documentLoadSampleRate ?? 0.1);
  // Resolved once: there is no process.env in the browser.
  const captureFullQuery = capturesFullPayloads(options.env);

  const propagation = {
    // Allowlist, not denylist — see ./ignoreUrls for the measurement behind it.
    ignoreUrls: firstPartyOnly([window.location.origin, options.apiOrigin]),
    // No Logfire-specific option exists; this is the standard OTel knob.
    propagateTraceHeaderCorsUrls: [apiPattern],
    clearTimingResources: true,
    // apps/api is cross-origin and sends no Timing-Allow-Origin header, so the
    // browser zeroes every PerformanceResourceTiming field it would otherwise
    // read. What survives is `fetchStart` and `responseEnd` — two events per
    // request that restate the span's own start and end. Set
    // Timing-Allow-Origin on apps/api and this becomes a real DNS/TLS/TTFB
    // breakdown worth turning back on; until then it is payload and noise.
    // Note this covers XHR and fetch only — documentLoad keeps its timings,
    // which are same-origin and genuinely useful.
    ignoreNetworkEvents: true,
    applyCustomAttributesOnSpan: (span: MutableClientSpan, request: unknown) =>
      annotateClientSpan(span, request, undefined, captureFullQuery),
  };

  const cleanup = logfire.configure({
    traceUrl: options.traceUrl,
    // Empty: the proxy attaches the real token server-side.
    traceExporterHeaders: () => ({}),

    // Runs before Logfire's exporting processor, so its mutations are exported.
    spanProcessors: [
      new ClientIdentitySpanProcessor(),
      new PruneClientSpanEventsProcessor(),
      new NotFoundIsNotAnErrorSpanProcessor(),
    ],

    serviceName: resource['service.name'] as string,
    serviceVersion: resource['service.version'] as string,
    environment: resource['deployment.environment.name'] as string,
    resourceAttributes: resource,

    scrubbing: {
      callback: ({ path, value }: { path: (string | number)[]; value: unknown }) =>
        scrubAttribute(path.join('.'), value).value,
    },

    autoInstrumentations: {
      // axios v1 uses the XHR adapter in browsers, and all ~200 API call sites
      // in apps/web go through the global axios default. THIS is the
      // instrumentation that actually produces the api-bound spans.
      '@opentelemetry/instrumentation-xml-http-request': { enabled: true, ...propagation },
      '@opentelemetry/instrumentation-fetch': { enabled: true, ...propagation },
      '@opentelemetry/instrumentation-document-load': { enabled: documentLoadEnabled },
      // PostHog already autocaptures clicks via the data-ph-capture-attribute-*
      // convention. Duplicating them here is cost with no new signal.
      '@opentelemetry/instrumentation-user-interaction': { enabled: false },
      '@opentelemetry/instrumentation-long-task': { enabled: false },
    },

    rum: {
      session: {
        // Firmable URLs carry company/person ids and search terms.
        urlAttributes: (url: URL) => ({
          full: `${url.origin}${templatizePath(url.pathname)}`,
          path: templatizePath(url.pathname),
        }),
        getRouteName: () => templatizePath(window.location.pathname),
        // Id only. The SDK's default getUser() would send name and email.
        getUser: () => {
          const { userId } = getTelemetryIdentity();
          return userId ? { id: userId } : undefined;
        },
      },
      // The 5 web-vital spans already carry the values; a metric reader would
      // bill roughly 60 more units per session for the same information.
      webVitals: { metrics: false },
      longAnimationFrames: false,
    },

    // Experimental, and rrweb on a PII-dense B2B data product conflicts
    // directly with the strict-PII bar.
    sessionReplay: false,
    console: false,
    minLevel: 'info',

    // Off by default in the browser SDK, on the reasoning that minified stacks
    // produce unstable fingerprints. Without it a client error is still
    // recorded — is_exception, message, stacktrace and all — but Logfire's
    // Error Tracking groups issues BY fingerprint, so none of them ever
    // becomes an issue and the page reads "No issues yet" while errors are
    // arriving. Verified by throwing a real uncaught error on preview: the
    // record landed, the issue did not.
    //
    // Error tracking is the headline thing Sentry did for us, so an empty
    // issues page is worse than fingerprints that occasionally split across a
    // deploy. reportClientError already folds the constructor name into the
    // message, so grouping stays legible even when a fingerprint churns.
    errorFingerprinting: true,
  } as never) as unknown as () => Promise<void>;

  setErrorReporter((message, error, attributes) => {
    reportClientError(message, error, attributes);
  });

  installGlobalErrorHandlers();
  return cleanup;
}

let handlersInstalled = false;

/**
 * The browser SDK installs no global handlers — reportError is entirely
 * manual. This is the single biggest Sentry capability that does not come for
 * free; without it, uncaught exceptions vanish silently.
 */
export function installGlobalErrorHandlers(): void {
  if (handlersInstalled || typeof window === 'undefined') return;
  handlersInstalled = true;

  window.addEventListener('error', (event) => {
    // Resource load failures (img, script tags) carry no `error`.
    if (!event.error) {
      logfire.warning('Resource load failed', {
        'resource.url': sanitizeUrl((event.target as HTMLElement & { src?: string })?.src ?? ''),
        'resource.tag': (event.target as HTMLElement)?.tagName ?? '',
      } as never);
      return;
    }
    if (isForeignExtensionError(event.error)) return;
    reportClientError('Uncaught exception', event.error, {
      'error.source': 'window.onerror',
      'code.filepath': sanitizeUrl(event.filename ?? ''),
      'code.lineno': event.lineno ?? 0,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isForeignExtensionError(event.reason)) return;
    reportClientError('Unhandled promise rejection', event.reason, {
      'error.source': 'unhandledrejection',
    });
  });
}

/**
 * The single funnel for every client-side error — global handlers, the shared
 * ErrorBoundary, and the Pages Router `_error` page.
 *
 * Two things it guarantees that a bare `logfire.reportError` does not:
 *
 * 1. GROUPING. Logfire's error tracking buckets by message, so a constant
 *    string like "Uncaught exception" collapses every distinct bug in the app
 *    into one issue. Folding the error's constructor name into the message
 *    ("Uncaught exception: TypeError") splits them into something triageable
 *    while staying low-cardinality — the name, never the message, which would
 *    carry ids and user input.
 * 2. SAMPLING. Errors are always kept. Without this, an error arriving on a
 *    sampled-out trace disappears, and error counts silently under-report.
 */
export function reportClientError(message: string, error: unknown, attributes: Record<string, unknown> = {}): void {
  const err = error instanceof Error ? error : new Error(String(error));
  const scrubbed = scrubAttributes(attributes).value;

  logfire.reportError(`${scrubText(message)}: ${err.name || 'Error'}`, err, {
    ...scrubbed,
    'error.type': err.name || 'Error',
    // Which page it happened on — templated, so ids never reach the attribute.
    'logfire.page.route': typeof window !== 'undefined' ? templatizePath(window.location.pathname) : '',
    // `firmable.user_id` / `firmable.org_id` match the api, so one filter spans all
    // three services. `user.id` stays alongside for Logfire's own browser UI, which
    // keys off it.
    ...identityAttributes(),
    ...(getTelemetryIdentity().userId ? { 'user.id': getTelemetryIdentity().userId } : {}),
    [Attr.FORCE_SAMPLE]: true,
  } as never);
}
